import { isPosition, positionLabel } from "../lib/listingKind";
/**
 * @file HomeCaseCard.jsx
 * @description The real open-case card used twice on the public homepage: as the
 *   single "Just posted" focal card in the hero, and (compact) in the Case
 *   Finder carousel. One component so both show the same facts, the same trust
 *   line and the same click behavior.
 *
 *   Shows ONLY what the public loader returns and anonymous visitors may read:
 *   type, city and state, pay, setting(s), hours, W2/1099, a fuzzed client age
 *   range, schedule days, true perks, and a Remote/Hybrid badge. Never client
 *   initials, agency identity, address, ZIP or free text. Nothing is invented:
 *   a fact the row does not carry is simply left out.
 *
 *   The whole card is clickable (mouse convenience); the "See this case" button
 *   is the keyboard/AT target and stops propagation so a click fires once.
 * @module components/HomeCaseCard
 */
import { useTranslation } from "react-i18next";
import { T, typeColor, typeLabel } from "../theme";
import {
  ClockIcon, CalendarIcon, ShieldIcon, CheckIcon,
  BriefcaseIcon, UsersIcon, HomeIcon, MonitorIcon,
} from "./Icons";
import { caseCardFacts, postedAgo, summarizeDays } from "../lib/homeCases";
import { settingLabel } from "../lib/settingLabels";

const NAVY       = "#0F1024";
const INDIGO     = T.indigo;
const GREEN      = T.green || "#0A6B4E";
const BORDER     = "rgba(226,232,240,0.8)";
const TEXT_MID   = "#475569";
const TEXT_LIGHT = "#94A3B8";

/** Reserved heights so the hero never jumps between skeleton and card. */
export const CARD_MIN_HEIGHT = 392;
export const CARD_MIN_HEIGHT_COMPACT = 352;

/** Same-size placeholder shown while the public cases load. Not a sample case. */
export function HomeCaseCardSkeleton({ compact = false }) {
  const bar = (w, h, mt = 0) => (
    <div style={{ width: w, height: h, borderRadius: 6, background: "#EEF1F6", marginTop: mt }} />
  );
  return (
    <div
      data-testid="home-case-skeleton"
      aria-hidden="true"
      style={{
        background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 18,
        padding: compact ? "18px 18px" : "24px 24px", boxSizing: "border-box",
        minHeight: compact ? CARD_MIN_HEIGHT_COMPACT : CARD_MIN_HEIGHT,
        boxShadow: "0 18px 50px rgba(15,16,36,0.28)",
      }}
    >
      {bar(120, 11)}
      {bar(70, 24, 16)}
      {bar("62%", compact ? 20 : 26, 16)}
      {bar("38%", compact ? 24 : 32, 12)}
      {bar("100%", 1, 20)}
      {bar("80%", 13, 18)}
      {bar("70%", 13, 12)}
      {bar("55%", 13, 12)}
    </div>
  );
}

export default function HomeCaseCard({ c, onOpen, compact = false, placement = "hero" }) {
  const { t } = useTranslation();
  const tc = typeColor(c.type);
  const f = caseCardFacts(c);
  const hasPay = !!(c.pay_range && String(c.pay_range).trim());
  const payColor = hasPay ? GREEN : TEXT_LIGHT;
  const cityState = [c.city, c.state].filter(Boolean).join(", ");

  // Label: "Just posted · 1 day ago" for the first week; honest "Posted 3 wk ago"
  // after that; plain "Open case" when there is no usable date.
  const ago = postedAgo(c.created_at);
  let label;
  if (!ago) label = isPosition(c) ? t("homeV2.positionLabelOpen") : t("homeV2.caseLabelOpen");
  else if (ago.key === "relativeTime.justNow") label = t("homeV2.caseJustPostedNow");
  else {
    const when = t(ago.key, { count: ago.count });
    label = ago.fresh ? t("homeV2.caseJustPosted", { when }) : t("homeV2.casePostedAgo", { when });
  }

  const days = summarizeDays(f.days);
  const dayLabel = (d) => t(`interview.dayShort.${d}`);
  const daysText = days.range
    ? t("homeV2.caseDaysRange", { from: dayLabel(days.range.from), to: dayLabel(days.range.to) })
    : days.list.map(dayLabel).join(", ");

  const empText = f.employment === "both" ? t("homeV2.caseEmpBoth")
    : f.employment === "1099" ? t("homeV2.caseEmp1099")
    : f.employment === "W2" ? t("homeV2.caseEmpW2") : null;

  const facts = [
    f.settings.length > 0 && { k: "setting", Icon: HomeIcon, text: f.settings.map((s) => settingLabel(t, s)).join(", ") },
    f.hours != null && { k: "hours", Icon: ClockIcon, text: t("homeV2.caseHours", { hours: f.hours }) },
    empText && { k: "emp", Icon: BriefcaseIcon, text: empText },
    f.ages && { k: "ages", Icon: UsersIcon, text: t("homeV2.caseAges", { range: f.ages }) },
    daysText && { k: "days", Icon: CalendarIcon, text: daysText },
  ].filter(Boolean);

  const perkText = {
    training: t("homeV2.casePerkTraining"),
    bcba: t("homeV2.casePerkBcba"),
    mileage: t("homeV2.casePerkMileage"),
  };

  const open = () => onOpen && onOpen(c, placement);
  const pad = compact ? "18px 18px" : "24px 24px";

  return (
    <article
      data-testid="home-case-card"
      data-placement={placement}
      onClick={open}
      style={{
        background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 18,
        padding: pad, boxSizing: "border-box", textAlign: "left", height: "100%",
        minHeight: compact ? CARD_MIN_HEIGHT_COMPACT : CARD_MIN_HEIGHT,
        display: "flex", flexDirection: "column", cursor: "pointer",
        boxShadow: compact ? "0 2px 14px rgba(15,16,36,0.06)" : "0 18px 50px rgba(15,16,36,0.28)",
      }}
    >
      {/* Freshness label: a plain fact from created_at, not a "live" claim. */}
      <div data-testid="home-case-label" style={{
        display: "flex", alignItems: "center", gap: 7, marginBottom: 14,
        fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase",
        color: INDIGO, fontFamily: T.fontMono,
      }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: INDIGO, flexShrink: 0 }} />
        {label}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <span style={{
          display: "inline-flex", alignItems: "center", fontSize: 11.5, fontWeight: 800,
          color: tc.accent, background: tc.bg, borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em",
        }}>
          {isPosition(c) ? positionLabel(t, c) : typeLabel(c.type)}
        </span>
        {f.delivery && (
          <span data-testid="home-case-delivery" style={{
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 800,
            color: "#1D4ED8", background: "#DBEAFE", borderRadius: 7, padding: "4px 10px",
          }}>
            <MonitorIcon size={11} color="#1D4ED8" />
            {f.delivery === "hybrid" ? t("remote.deliveryHybrid") : t("remote.deliveryRemote")}
          </span>
        )}
      </div>

      <h3 style={{
        margin: "0 0 6px", fontSize: compact ? 18 : 24, fontWeight: 900, color: NAVY,
        letterSpacing: "-0.02em", lineHeight: 1.2,
      }}>
        {cityState}
      </h3>

      <div style={{
        color: payColor,
        fontSize: compact ? 20 : 30, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.15,
        marginBottom: 14,
      }}>
        {/* pay_range already arrives fully formatted from the DB (e.g.
            "$65–100/hr"), so no unit suffix is appended here. */}
        {hasPay ? c.pay_range : t("homeV2.payTbd")}
      </div>

      {facts.length > 0 && (
        <ul data-testid="home-case-facts" style={{
          listStyle: "none", margin: "0 0 12px", padding: "14px 0 0",
          borderTop: `1px solid ${BORDER}`, display: "grid",
          gridTemplateColumns: compact ? "1fr" : "1fr 1fr", gap: "9px 14px",
        }}>
          {facts.map(({ k, Icon, text }) => (
            <li key={k} data-fact={k} style={{
              display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13.5, fontWeight: 600,
              color: TEXT_MID, lineHeight: 1.35, gridColumn: k === "days" && !compact ? "1 / -1" : "auto",
            }}>
              <Icon size={14} color={INDIGO} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{text}</span>
            </li>
          ))}
        </ul>
      )}

      {f.perks.length > 0 && (
        <div data-testid="home-case-perks" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {f.perks.map((p) => (
            <span key={p} style={{
              display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 700,
              color: GREEN, background: "#ECFDF5", borderRadius: 999, padding: "3px 9px",
            }}>
              <CheckIcon size={11} color={GREEN} /> {perkText[p]}
            </span>
          ))}
        </div>
      )}

      {/* True for every posted case: posting requires a verified NPI (enforce_case_npi_gate). */}
      <div data-testid="home-case-trust" style={{
        display: "flex", alignItems: "center", gap: 7, marginTop: "auto", marginBottom: 14,
        paddingTop: 4, fontSize: 12.5, fontWeight: 700, color: TEXT_MID,
      }}>
        <ShieldIcon size={14} color={INDIGO} style={{ flexShrink: 0 }} />
        {t("homeV2.caseNpiVerified")}
      </div>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); open(); }}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
          width: "100%", padding: compact ? "10px 16px" : "13px 18px",
          background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 11,
          fontSize: compact ? 13 : 14.5, fontWeight: 800, cursor: "pointer", fontFamily: T.fontBody,
        }}
      >
        {t(isPosition(c) ? "homeV2.caseCtaPosition" : "homeV2.caseCta")} →
      </button>
    </article>
  );
}
